import CartOrderPageSummary from "components/cart/CartOrderPageSummary";
import Breadcrumbs from "components/general/Breadcrumbs";
import Container from "components/general/Container";

import PageHeader from "components/general/Headers/PageHeader";
import OrderProgress from "components/general/OrderProgress";
import PageLoader from "components/general/PageLoader";
import ScrollContainer from "components/general/ScrollContainer";
import { Box, FormattedCurrency, HStack, VStack } from "components/ui";
import { H3, Paragraph, Typography } from "components/ui/Typography";
import { useLocalSearchParams } from "expo-router";
import React from "react";
import { ActivityIndicator } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useGetOrderByDocumentNumberQuery } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { FlatList, H1, View } from "../components/ui";

export function OrderScreen() {
	const { id, message, cartId } = useLocalSearchParams<{ id: string; message: string; cartId: string }>();

	if (id == null) return;
	const isMobile = useIsMobile();
	const {
		data: order,
		isLoading,
		isFetching,
	} = useGetOrderByDocumentNumberQuery({
		documentNumber: id,
	});

	if (isLoading || isFetching) {
		return <PageLoader />;
	}

	if (isMobile) {
		return (
			<SafeAreaView style={{ height: "100%", backgroundColor: "white" }}>
				<Box style={{ flex: 1 }}>
					<FlatList
						data={order?.lineCollection}
						scrollEnabled={true}
						initialNumToRender={50}
						maxToRenderPerBatch={50}
						windowSize={50}
						ListHeaderComponent={() => {
							return (
								<View bg="$basicwhite">
									<Box paddingHorizontal={12} paddingBottom={24}>
										<Breadcrumbs />
									</Box>

									{message && cartId && (
										<Box paddingHorizontal={12} paddingBottom={24}>
											<Paragraph> Your order with reference # {cartId} has been completed </Paragraph>
										</Box>
									)}

									<Box paddingHorizontal={12} paddingBottom={24}>
										<H1>Order number : {order?.documentNumber} </H1>
									</Box>
									<OrderProgress orderStatus={order?.status} />
									<Box borderBottomWidth={1} borderBottomColor="$neutral100" alignItems="flex-end" paddingHorizontal={12} paddingVertical={32}>
										<HStack w="$full" justifyContent="space-between">
											<H3>Order Items</H3>
											<Typography.Paragraph weight="normal">{order?.lineCollection?.length || 0} Item(s)</Typography.Paragraph>
										</HStack>
									</Box>
								</View>
							);
						}}
						renderItem={({ item, index }: any) => {
							return (
								<Box
									key={index}
									bg="$basicwhite"
									borderRadius={12}
									paddingVertical={20}
									paddingHorizontal={24}
									marginHorizontal={12}
									marginVertical={6}
									borderWidth={1}
									borderColor="$neutral100"
									shadowColor="$neutral200"
									shadowOffset={{ width: 0, height: 2 }}
									shadowOpacity={0.05}
									shadowRadius={8}
									elevation={2}
								>
									<HStack justifyContent="space-between" alignItems="center">
										<Box flex={2}>
											<Typography.Paragraph
												sx={{
													fontFamily: "Roboto_700Bold",
													color: "$neutral700",
													fontSize: "$paragraphdesktop",
												}}
											>
												{item.stockcode}
											</Typography.Paragraph>
										</Box>
										<Box flex={4}>
											<Typography.Paragraph
												sx={{
													color: "$neutral600",
													lineHeight: 24,
													fontSize: "$paragraphdesktop",
												}}
											>
												{item.description}
											</Typography.Paragraph>
										</Box>
										<Box flex={1} alignItems="center">
											<Typography.Paragraph
												sx={{
													fontFamily: "Roboto_700Bold",
													color: "$neutral700",
													fontSize: "$paragraphdesktop",
												}}
											>
												{item.quantity}
											</Typography.Paragraph>
										</Box>
										{order?.status === "Partially Delivered" && (
											<Box flex={1} alignItems="center">
												<Typography.Paragraph
													sx={{
														fontFamily: "Roboto_700Bold",
														color: "$neutral700",
														fontSize: "$paragraphdesktop",
													}}
												>
													{item.suppliedQuantity}
												</Typography.Paragraph>
											</Box>
										)}
										<Box flex={1} alignItems="flex-end">
											<Typography.Paragraph
												sx={{
													fontFamily: "Roboto_700Bold",
													color: "$primarylight",
													fontSize: "$paragraphdesktop",
												}}
											>
												<FormattedCurrency amount={item.total} />
											</Typography.Paragraph>
										</Box>
									</HStack>
								</Box>
							);
						}}
						ListEmptyComponent={
							<>
								<Box gap={16} bg="$neutral25" paddingVertical={28} paddingHorizontal={20}>
									<Box>
										<H3>No order items</H3>
										<Typography sx={{ lineHeight: 24, fontFamily: "Roboto_400Regular" }} weight="normal">
											This order appears to be empty or the items could not be loaded.
										</Typography>
									</Box>
								</Box>
							</>
						}
					/>
				</Box>

				<CartOrderPageSummary />
			</SafeAreaView>
		);
	}

	return (
		<OrderLayout>
			<Box width="100%">
				<Box paddingVertical={20} paddingHorizontal={24} marginBottom={24}>
					<HStack justifyContent="space-between" alignItems="center">
						<Box flex={2}>
							<Typography.H5 color="$neutral700">Stock Code</Typography.H5>
						</Box>
						<Box flex={4}>
							<Typography.H5 color="$neutral700">Description</Typography.H5>
						</Box>
						<Box flex={1} alignItems="center">
							<Typography.H5 color="$neutral700">Qty</Typography.H5>
						</Box>
						{order?.status === "Partially Delivered" && (
							<Box flex={1} alignItems="center">
								<Typography.H5 color="$neutral700">Delivered</Typography.H5>
							</Box>
						)}
						<Box flex={1} alignItems="flex-end">
							<Typography.H5 color="$neutral700">Total</Typography.H5>
						</Box>
					</HStack>
				</Box>

				<VStack space="md">
					{order?.lineCollection?.map((item, index) => (
						<Box
							key={index}
							bg="$basicwhite"
							borderRadius={12}
							paddingVertical={20}
							paddingHorizontal={24}
							borderWidth={1}
							borderColor="$neutral100"
							shadowColor="$neutral200"
							shadowOffset={{ width: 0, height: 2 }}
							shadowOpacity={0.05}
							shadowRadius={8}
							elevation={2}
						>
							<HStack justifyContent="space-between" alignItems="center">
								<Box flex={2}>
									<Typography.Paragraph
										sx={{
											fontFamily: "Roboto_700Bold",
											color: "$neutral700",
											fontSize: "$paragraphdesktop",
										}}
									>
										{item.stockcode}
									</Typography.Paragraph>
								</Box>
								<Box flex={4}>
									<Typography.Paragraph
										sx={{
											color: "$neutral600",
											lineHeight: 24,
											fontSize: "$paragraphdesktop",
										}}
									>
										{item.description}
									</Typography.Paragraph>
								</Box>
								<Box flex={1} alignItems="center">
									<Typography.Paragraph
										sx={{
											fontFamily: "Roboto_700Bold",
											color: "$neutral700",
											fontSize: "$paragraphdesktop",
										}}
									>
										{item.quantity}
									</Typography.Paragraph>
								</Box>
								{order?.status === "Partially Delivered" && (
									<Box flex={1} alignItems="center">
										<Typography.Paragraph
											sx={{
												fontFamily: "Roboto_700Bold",
												color: "$neutral700",
												fontSize: "$paragraphdesktop",
											}}
										>
											{item.suppliedQuantity}
										</Typography.Paragraph>
									</Box>
								)}
								<Box flex={1} alignItems="flex-end">
									<Typography.Paragraph
										sx={{
											fontFamily: "Roboto_700Bold",
											color: "$primarylight",
											fontSize: "$paragraphdesktop",
										}}
									>
										<FormattedCurrency amount={item.total} />
									</Typography.Paragraph>
								</Box>
							</HStack>
						</Box>
					))}
				</VStack>
			</Box>
		</OrderLayout>
	);
}

export function OrderLayout({ children }: { children: React.ReactNode }) {
	const { id, message, cartId } = useLocalSearchParams<{ id: string; message: string; cartId: string }>();
	if (id == null) return;
	const isMobile = useIsMobile();
	const { data, isLoading, isFetching } = useGetOrderByDocumentNumberQuery({
		documentNumber: id,
	});

	if (isLoading || isFetching) {
		return (
			<ScrollContainer>
				<Box minHeight={400} justifyContent="center" alignItems="center" padding={20}>
					<ActivityIndicator size="large" style={{ transform: [{ scale: 2 }] }} />
				</Box>
			</ScrollContainer>
		);
	}
	return (
		<ScrollContainer>
			<Container>
				{message && cartId && (
					<Box paddingVertical={24}>
						<Paragraph sx={{ bg: "$successlight", padding: 16 }}> Your order with reference # {cartId} has been completed </Paragraph>
					</Box>
				)}
				<PageHeader title={`Order number : ${data?.documentNumber}`} />

				<Box paddingHorizontal={isMobile ? 8 : 0}>
					<OrderProgress orderStatus={data?.status} />
				</Box>

				<Box flexDirection="row" mb={32} gap={32}>
					<Box flex={1}>{children}</Box>
					<CartOrderPageSummary />
				</Box>
			</Container>
		</ScrollContainer>
	);
}
